import React, { useState, useEffect } from 'react';
import api from '../config/axios';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';
import { XMarkIcon, CreditCardIcon, ShieldCheckIcon } from '@heroicons/react/24/outline';

const PaymentModal = ({ isOpen, onClose, subscription, onPaymentSuccess }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');

  useEffect(() => {
    if (user?.email) {
      setEmail(user.email);
    }
  }, [user]);

  const handleInitializePayment = async () => {
    if (!email) {
      toast.error('Please enter your email address');
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/payments/initialize', {
        subscriptionId: subscription.id,
        email: email,
        callbackUrl: `${window.location.origin}/payments/callback`
      });

      const { authorizationUrl } = response.data.payment;
      window.location.href = authorizationUrl;
    } catch (error) {
      console.error('Payment initialization error:', error);
      toast.error(error.response?.data?.message || 'Failed to initialize payment. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount, currency = 'ZAR') => {
    return new Intl.NumberFormat('en-ZA', {
      style: 'currency',
      currency: currency,
    }).format(amount);
  };

  if (!isOpen || !subscription) return null;

  return (
    <div className="fixed inset-0 bg-neutral-900/60 backdrop-blur-sm overflow-y-auto h-full w-full z-50 flex items-start justify-center pt-20">
      <div className="relative w-full max-w-md mx-4 animate-fade-in">
        <div className="card">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center shadow-orange">
                <CreditCardIcon className="w-5 h-5 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-neutral-800">Complete Payment</h3>
            </div>
            <button
              onClick={onClose}
              className="text-neutral-400 hover:text-neutral-600 transition-colors"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          {/* Plan Details */}
          <div className="bg-gradient-to-br from-primary-50 to-white p-5 rounded-xl border border-primary-100 mb-6">
            <p className="text-sm font-medium text-neutral-500 mb-1">Selected Plan</p>
            <p className="text-lg font-bold text-neutral-800">{subscription.planName}</p>
            <p className="text-3xl font-bold text-primary-600 mt-2">
              {formatCurrency(subscription.price, subscription.currency)}
            </p>
            <p className="text-sm text-neutral-500">per month</p>
          </div>

          {/* Email Input */}
          <div className="mb-6">
            <label htmlFor="payment-email" className="block text-sm font-medium text-neutral-700 mb-2">
              Email Address
            </label>
            <input
              type="email"
              id="payment-email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="input-field"
              placeholder="Enter your email address"
              required
            />
            <p className="text-xs text-neutral-400 mt-1">Payment receipt will be sent to this email</p>
          </div>

          {/* Security Info */}
          <div className="flex items-start gap-3 bg-blue-50 p-4 rounded-xl border border-blue-100 mb-6">
            <ShieldCheckIcon className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-blue-800">
              You will be redirected to Paystack's secure payment page to complete your transaction.
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="btn-secondary flex-1"
            >
              Cancel
            </button>
            <button
              onClick={handleInitializePayment}
              disabled={loading || !email}
              className="btn-primary flex-1"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="spinner spinner-sm" />
                  Processing...
                </span>
              ) : (
                `Pay ${formatCurrency(subscription.price, subscription.currency)}`
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentModal;
